// apps/client/src/characters/head.ts — the doll's big rounded head and a face that reads from the town camera (characters pass, 2026-10-16).
// The town camera looks down at tuning.camera.pitchDeg (52°), so a face drawn at the head's equator is a squashed crescent under a cap of
// hair ("a brown sphere on a box"). The head group tips the chin up a little and the features sit above the equator, sized to read at the
// default camera distance; from the creator's level view the face still sits where a chibi face belongs.
import * as THREE from 'three';
import { luma, part, shade, type V3 } from './part';
import { blob, ellipsoidPoint } from './shapes';

/** The head in its slot (head-bone space, rig units): centre, radii, and how far the whole head tips the chin up. */
export const HEAD = { c: new THREE.Vector3(0, 0.13, 0.012), r: new THREE.Vector3(0.16, 0.146, 0.152), tilt: 0.1 } as const;
const Z = new THREE.Vector3(0, 0, 1);
const INK = '#2B2118', BLUSH = '#F3A9A0', MOUTH = '#6B3F30', GLINT = '#FFFFFF';

/** A point on the head at latitude `lat` (+ up) and longitude `lon` (0 = front, + the doll's left), lifted `lift` along the surface normal,
 *  in head-slot space, and the outward normal there. */
export function onHead(lat: number, lon: number, lift = 0): { p: THREE.Vector3; n: THREE.Vector3 } {
  const { p, n } = ellipsoidPoint(HEAD.r, lat, lon);
  p.add(HEAD.c).addScaledVector(n, lift);
  return { p, n };
}
/** A part on the head's surface at (lat, lon): its local +z along the surface normal, then turned `roll` about it. */
export function stick(head: THREE.Object3D, geo: THREE.BufferGeometry, hex: string, lat: number, lon: number, lift = 0, name = '', roll = 0): THREE.Mesh {
  const { p, n } = onHead(lat, lon, lift);
  const m = part(head, geo, hex, [p.x, p.y, p.z], name);
  m.quaternion.setFromUnitVectors(Z, n);
  if (roll) m.quaternion.multiply(new THREE.Quaternion().setFromAxisAngle(Z, roll));
  return m;
}
/** The head ball itself (named `head-shape`). */
export function headShape(head: THREE.Object3D, hex: string): THREE.Mesh {
  const at: V3 = [HEAD.c.x, HEAD.c.y, HEAD.c.z];
  return part(head, blob(HEAD.r.x, HEAD.r.y, HEAD.r.z, 20, 14), hex, at, 'head-shape');
}

export interface FaceSpec {
  /** 'round' | 'happy' | 'sleepy' | 'sparkle'; anything else draws 'round'. */
  eyes?: string;
  /** The face's own colour: on a dark face the eyes get white rims so they still read. */
  skin: string;
  /** Where the eyes sit (radians on the head); animals' are a touch wider and lower to leave room for a muzzle. */
  eyeLat?: number; eyeLon?: number;
  /** Mouth latitude, or null for none (a muzzle or bill carries its own). */
  mouthLat?: number | null;
  blush?: boolean;
}
export const EYE_LAT = 0.12, EYE_LON = 0.36;
/** Eyes (named eye-left / eye-right), blush (blush-left / blush-right) and a small smile (mouth). */
export function face(head: THREE.Object3D, f: FaceSpec): void {
  const lat = f.eyeLat ?? EYE_LAT, lon = f.eyeLon ?? EYE_LON, dark = luma(f.skin) < 0.25;
  for (const [side, sx] of [['left', 1], ['right', -1]] as const) eye(head, f.eyes, lat, sx * lon, side, dark);
  if (f.blush ?? true) for (const [side, sx] of [['left', 1], ['right', -1]] as const)
    stick(head, blob(0.027, 0.016, 0.006, 10, 6), dark ? shade(BLUSH, 0.85) : BLUSH, lat - 0.2, sx * (lon + 0.27), -0.001, `blush-${side}`);
  const mouth = f.mouthLat === undefined ? lat - 0.29 : f.mouthLat;
  if (mouth !== null) smile(head, mouth, 0, { name: 'mouth' });
}
/** A small closed smile (an arc bowing down) at (lat, lon), `lift` off the surface (on a muzzle, its depth). */
export function smile(head: THREE.Object3D, lat: number, lon: number, o: { r?: number; name?: string; lift?: number } = {}): THREE.Mesh {
  return stick(head, new THREE.TorusGeometry(o.r ?? 0.016, 0.0048, 4, 8, Math.PI), MOUTH, lat, lon, o.lift ?? 0.001, o.name ?? '', Math.PI);
}
function eye(head: THREE.Object3D, style: string | undefined, lat: number, lon: number, side: string, dark: boolean): void {
  const name = `eye-${side}`;
  const glint = (dx: number, dy: number, r: number) => {
    const { p, n } = onHead(lat + dy, lon + dx, 0.011);
    part(head, blob(r, r, r * 0.6, 6, 4), GLINT, [p.x, p.y, p.z]).quaternion.setFromUnitVectors(Z, n);
  };
  if (dark && style !== 'happy' && style !== 'sleepy') stick(head, blob(0.03, 0.039, 0.008, 10, 6), '#FFF8EC', lat, lon, -0.001);   // a light rim
  switch (style) {
    case 'happy':                                                                  // a closed, smiling eye: an arc bowing up
      stick(head, new THREE.TorusGeometry(0.02, 0.0068, 4, 8, Math.PI), INK, lat - 0.04, lon, 0.002, name);
      break;
    case 'sleepy':                                                                 // half-lidded: a low, wide oval
      stick(head, blob(0.024, 0.0145, 0.01, 8, 6), INK, lat - 0.05, lon, 0.002, name);
      glint(0.03, -0.05, 0.0045);
      break;
    case 'sparkle':                                                                // a big oval with two glints
      stick(head, blob(0.025, 0.034, 0.012, 8, 6), INK, lat, lon, 0.002, name);
      glint(0.04, 0.09, 0.0085);
      glint(-0.035, -0.07, 0.005);
      break;
    default:                                                                       // round
      stick(head, blob(0.022, 0.031, 0.012, 8, 6), INK, lat, lon, 0.002, name);
      glint(0.035, 0.085, 0.0075);
  }
}

/** A resident's ears: small rounded half-discs at the sides of the head, under most hair. */
export function personEars(head: THREE.Object3D, skin: string): void {
  for (const [side, sx] of [['left', 1], ['right', -1]] as const)
    stick(head, blob(0.028, 0.036, 0.016, 8, 6), shade(skin, 0.96), -0.03, sx * 1.5, -0.004, `ear-${side}`);
}
/** A tiny nose bump, just enough to catch the light in the creator's close view. */
export function nose(head: THREE.Object3D, skin: string, lat = EYE_LAT - 0.16): void {
  stick(head, blob(0.014, 0.011, 0.01, 8, 5), shade(skin, 0.97), lat, 0, 0, 'nose');
}
